import { CircleAlert, Info, AlertTriangle, CheckCircle } from 'lucide-react-native';

/** The four semantic feedback tones an announcement can carry. */
export type AlertTone = 'error' | 'warning' | 'info' | 'success';

/**
 * The wire format for `variant`, mirroring `ClientAnnouncementConfig` in the
 * generated API types. The backend can send any of these eight, so the public
 * shape stays as-is even though the `-dark` half only overrides text color.
 */
export type AlertVariant = AlertTone | `${AlertTone}-dark`;

/**
 * Everything a tone decides. The `-dark` half of the wire format contributes
 * nothing here, which is the reason it is a boolean rather than four more
 * entries in this table.
 */
export const TONE_STYLES = {
  error: { icon: CircleAlert, background: 'bg-background-error' },
  warning: { icon: AlertTriangle, background: 'bg-background-warning' },
  info: { icon: Info, background: 'bg-background-info' },
  success: { icon: CheckCircle, background: 'bg-background-success' },
} as const;

const DARK_VARIANT_SUFFIX = '-dark';

/**
 * The text color a `-dark` variant applies over the tone's own.
 *
 * Note this is the only thing `-dark` does. The banner's ordinary text color
 * comes from the `Alert` primitive's `action`, not from a typography token, so
 * a `-dark` banner is the one case where the two disagree.
 */
export const DARK_VARIANT_TEXT_COLOR = 'text-typography-900';

/**
 * The tone a variant falls back to when the wire value names one this build does
 * not have. Matches the banner's own default so an unrecognised variant reads as
 * an ordinary announcement rather than as an error.
 */
const FALLBACK_TONE: AlertTone = 'success';

/**
 * Splits a wire variant into the two things it actually encodes.
 *
 * Total for any input, and it has to stay that way. `AlertVariant` is a
 * compile-time promise about a value this app receives from another system at
 * runtime, so the backend can ship a ninth variant, a typo, or an explicit JSON
 * `null`, without this build knowing about it. Each of those reaches here as a
 * value the type says is impossible.
 *
 * `null` is the one that slips past a default parameter, which only fills in for
 * `undefined` — so a caller spreading a config straight through hands this a
 * `null` that no default catches. Both that and an unrecognised tone fall back
 * rather than reaching the tone table with a key that is not in it, which threw
 * and took the whole banner down with it even when the announcement carried an
 * emoji and never needed the tone's icon at all.
 */
export function parseAlertVariant(variant: AlertVariant): { tone: AlertTone; isDark: boolean } {
  if (typeof variant !== 'string') {
    return { tone: FALLBACK_TONE, isDark: false };
  }

  const isDark = variant.endsWith(DARK_VARIANT_SUFFIX);

  const candidate = isDark ? variant.slice(0, -DARK_VARIANT_SUFFIX.length) : variant;

  return {
    tone: candidate in TONE_STYLES ? (candidate as AlertTone) : FALLBACK_TONE,
    isDark,
  };
}
